import React, { createContext, useContext, useRef, useState, useEffect } from "react";

import dayjs from "dayjs";
import Hls from 'hls.js'

const VideoContext = createContext();

export const VideoProvider = ({ children }) => {
    const videoRefs = useRef([]);
    const hlsRefs = useRef([])
    const [playing, setPlaying] = useState(true)
    const playingRef = useRef(true)
    const [masterSeekPosition, setMasterSeekPosition] = useState(0)
    const [masterDuration, setMasterDuration] = useState(0)
    const [masterLivePosition, setMasterLivePosition] = useState(0)
    const [linkedStreams, setLinkedStreams] = useState([])
    const [masterIsLive, setMasterIsLive] = useState(true)
    const [masterIsSeeking, setMasterIsSeeking] = useState(false)
    const masterPlaybackModeRef = useRef('live')
    const [masterPlaybackStart, setMasterPlaybackStart] = useState()
    const [masterPlaybackEnd, setMasterPlaybackEnd] = useState(dayjs().unix())
    const masterSpeed = useRef("1")
    const [streamErrors, setStreamErrors] = useState({});
    const [cancelSignal, setCancelSignal] = useState(null)
    const [streamTokens, setStreamTokens] = useState(null);
    const hlsHighResRef = useRef()
    const highResStartRef = useState(false)
    const [streamLoading, setStreamLoading] = useState({})
    const [windowStartMs, setWindowStartMs] = useState(null);
    const windowStartMsRef = useRef(null);
    const [currentTimePointer, setCurrentTimePointer] = useState(Date.now()); // ms
    const [scrollWheelStart, setScrollWheelStart] = useState({})
    const lastKnownPointerRef = useRef(null);
    const [videoStates, setVideoStates] = useState({});
    const [groupPlaying, setGroupPlaying] = useState(false);
    const [areListenersSetup, setListenersSetup] = useState({})
    const [leaderVideoCurrentTime, setLeaderVideoCurrentTime] = useState(0);
    const isHighspeed = useRef(false)

    let token = localStorage.getItem('token');

    useEffect(() => {
        windowStartMsRef.current = windowStartMs;
    }, [windowStartMs]);

    const initializeHLS = (index, stream, {highresStream = null, resumeAbsMs = currentTimePointer} = {}) => {
        setStreamLoading(prev => ({ ...prev, [index]: 'Loading...' }))
        destroyHLS(index)
        const liveConfig = {

            // --- Loading / playback ---
            autoStartLoad: false,            // manual control
            debug: false,

            // --- Quality / buffer management ---
            capLevelOnFPSDrop: false,
            capLevelToPlayerSize: false,
            defaultAudioCodec: undefined,
            initialLiveManifestSize: 1,
            maxBufferLength: 20,              // max buffer in seconds
            maxMaxBufferLength: 60,
            backBufferLength: 10,             // keep 10s of past content
            frontBufferFlushThreshold: Infinity,
            maxBufferSize: 60 * 1000 * 1000,  // 60MB
            maxBufferHole: 1,
            highBufferWatchdogPeriod: 2,      // check buffer frequently

            // --- Live playback tuning ---
            liveSyncDuration: 8,              // aim ~8s behind live
            liveMaxLatencyDuration: 10,        // snap forward if >10s behind live
            liveSyncOnStallIncrease: 1,       // increase sync if stall occurs
            nudgeOffset: 0.2,                 // jump forward increment
            nudgeMaxRetry: 10,                // retry nudging if behind live
            maxFragLookUpTolerance: 0.25,     // segment lookup tolerance

            // --- Performance / security ---
            preferManagedMediaSource: false,
            enableWorker: true,               // offload parsing/decoding
            enableSoftwareAES: true,          // JS decryption for encrypted streams
            manifestLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 9000,
                    maxLoadTimeMs: 20000,
                    timeoutRetry: {
                        maxNumRetry: 5,
                        retryDelayMs: 0,
                        maxRetryDelayMs: 0,
                    },
                    errorRetry: {
                        maxNumRetry: 5,
                        retryDelayMs: 1000,
                        maxRetryDelayMs: 8000,
                    },
                },
            },
            fragLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 2000,
                    maxLoadTimeMs: 3000,
                    timeoutRetry: {
                        maxNumRetry: 3,
                        retryDelayMs: 0,
                        maxRetryDelayMs: 0,
                    },
                    errorRetry: {
                        maxNumRetry: 3,
                        retryDelayMs: 1500,
                        maxRetryDelayMs: 3000,
                        backoff: 'linear',
                    },
                },
            },
            xhrSetup: xhr => {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`)
            }
        };

        const vodConfig = {
            // --- Loading / playback ---
            autoStartLoad: false,            // manual control
            startPosition: -1,                // jump immediately to live
            debug: false,
            maxBufferLength: 10,              // max buffer in seconds
            maxMaxBufferLength: 12,
            backBufferLength: Infinity,             // keep 10s of past content
            frontBufferFlushThreshold: Infinity,
            highBufferWatchdogPeriod: 2,      // check buffer frequently

            // --- Performance / security ---
            preferManagedMediaSource: false,
            enableWorker: true,               // offload parsing/decoding
            enableSoftwareAES: true,          // JS decryption for encrypted streams
            manifestLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 9000,
                    maxLoadTimeMs: 20000,
                    timeoutRetry: {
                        maxNumRetry: 5,
                        retryDelayMs: 0,
                        maxRetryDelayMs: 0,
                    },
                    errorRetry: {
                        maxNumRetry: 5,
                        retryDelayMs: 1000,
                        maxRetryDelayMs: 8000,
                    },
                },
            },
            fragLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 9000,
                    maxLoadTimeMs: 100000,
                    timeoutRetry: {
                        maxNumRetry: 5,
                        retryDelayMs: 0,
                        maxRetryDelayMs: 0,
                    },
                    errorRetry: {
                        maxNumRetry: 5,
                        retryDelayMs: 3000,
                        maxRetryDelayMs: 15000,
                        backoff: 'linear',
                    },
                },
            },
            xhrSetup: xhr => {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`)
            }

        }

        const video = videoRefs.current[index];
        const config = masterIsLive ? liveConfig : vodConfig

        const hls = new Hls(
            config
        );

        hlsRefs.current[index] = hls;

        if (Hls.isSupported()) {
            // Hls.js support
            hls.loadSource(stream);
            hls.attachMedia(video);

            // Optional: Handle events
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (masterIsLive) {
                    hls.startLoad(-1);
                }
                setStreamLoading(prev => ({ ...prev, [index]: null }))
                setStreamErrors(prev => ({ ...prev, [index]: null }))
                // Resume only for playback mode
                if (resumeAbsMs && windowStartMsRef.current != null && masterPlaybackModeRef.current === "playback") {
                    const trySeek = () => {
                        if (video.seekable.length > 0) {
                            const relSec = (resumeAbsMs - windowStartMsRef.current) / 1000;
                            const start = video.seekable.start(0);
                            const end = video.seekable.end(0);
                            // Clamp to available range
                            const clamped = Math.min(Math.max(relSec, start), end - 1);

                            video.currentTime = clamped;
                            video.playbackRate = masterSpeed.current
                            video.removeEventListener("canplay", trySeek);
                        }
                    };

                    //safer than loadedmetadata → wait until we can actually seek
                    video.addEventListener("canplay", trySeek);
                }

                // Update duration when metadata is available
                if (index === 0 && masterPlaybackModeRef.current === 'live' && highresStream != null) { //!= null will also catch undefined
                    if (!highResStartRef.current) {
                        const highResSrc = highresStream
                        highResStartRef.current = true;
                        const highResHls = new Hls(config);
                        hlsHighResRef.current = highResHls;

                        // Preload high-res
                        highResHls.loadSource(highResSrc);
                        highResHls.attachMedia(document.createElement('video')); // Dummy element to preload
                        highResHls.on(Hls.Events.MANIFEST_PARSED, () => {
                            // Optional: Auto-switch after preload
                            setTimeout(() => {

                                hls.loadSource(highResSrc);
                                highResHls.stopLoad()
                                highResHls.destroy()
                                highResHls.detachMedia()

                            }, 3000);
                        });
                    }
                }
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                //console.error(`HLS error [${index}]`, data);

                const isFatal = data?.fatal;
                const errorType = data?.type;
                const details = data?.details;
                const errorMsg = data?.error?.message;

                //console.log(`[${index}] Error Type: ${errorType}, Details: ${details}, Fatal: ${isFatal}`);
                if (errorMsg) console.log(`[${index}] Message: ${errorMsg}`);

                if (isFatal) {
                    switch (errorType) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            //console.warn(`[${index}] Fatal network error – restarting HLS`)
                            setStreamErrors(prev => ({ ...prev, [index]: 'Load Error' }))
                            hlsRefs.current[index].destroy()
                            hlsRefs.current[index] = null
                            initializeHLS(index, stream)
                            break

                        case Hls.ErrorTypes.MEDIA_ERROR:
                            //console.warn(`[${index}] Fatal media error – restarting HLS`)
                            setStreamErrors(prev => ({ ...prev, [index]: 'Video Error' }))
                            hlsRefs.current[index].destroy();
                            hlsRefs.current[index] = null;
                            initializeHLS(index, stream);
                            break

                        default:
                            //console.warn(`[${index}] Fatal error of unknown type – destroying HLS`)
                            setStreamErrors(prev => ({ ...prev, [index]: 'Unknown Fatal Error' }))
                            if (hlsRefs.current[index]) {
                                hlsRefs.current[index].destroy()
                                hlsRefs.current[index] = null
                            }
                            break
                    }
                } else {

                    if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
                        setStreamLoading(prev => ({ ...prev, [index]: 'Buffering...' }))
                        //console.warn(`[${index}] Buffer stall detected:`, data);
                        handleBufferStall(hls, video, index, stream)
                    }

                    // Non-fatal errors can be recovered automatically
                    if (errorType === Hls.ErrorTypes.NETWORK_ERROR) {
                        //console.log(`[${index}] Non-fatal network error - trying startLoad`)
                        hls.startLoad()
                    } else if (errorType === Hls.ErrorTypes.MEDIA_ERROR) {
                        //console.log(`[${index}] Non-fatal media error - trying recoverMediaError`)
                        hls.recoverMediaError()
                    }

                    // If error occurred on main feed, allow high res stream to be re-fetched
                    if (index === 0) {
                        highResStartRef.current = undefined;
                    }
                }
            });


        } else {
            video.src = stream;
        }

    }

    let stallRecoveryAttempts = {};

    const handleBufferStall = (hls, video, index, stream) => {
        if (!hls || !video) return;

        // Prevent overlapping retries
        if (stallRecoveryAttempts[index]?.inProgress) return;

        stallRecoveryAttempts[index] = {
            inProgress: true,
            attempts: 0,
            maxAttempts: 3
        };

        const retry = () => {
            const { attempts, maxAttempts } = stallRecoveryAttempts[index];
            if (attempts >= maxAttempts) {
                const resumeAbs = lastKnownPointerRef.current;
                //console.warn(`[${index}] Max buffer stall attempts reached. Restarting stream.`);
                hls.destroy();
                hlsRefs.current[index] = null;
                
                initializeHLS(index, stream, {resumeAbsMs: resumeAbs})
                //console.log(`Restored at ${dayjs(resumeAbs).format('HH:mm:ss')}`)
                const v = videoRefs.current[index];
                if (v) attachVideoListeners(v, index);
                stallRecoveryAttempts[index].inProgress = false;
                return;
            }

            stallRecoveryAttempts[index].attempts++;

            const currentTime = video.currentTime;

            // Check if player is truly stalled
            if (video.readyState < 3) {
                if (masterIsLive && hls.liveSyncPosition) {
                    // Live → snap to live edge
                    video.currentTime = video.seekable.end(0) - 2; //why 2s behind?
                } else {
                    // Playback → stay close to last known pointer
                    if (lastKnownPointerRef.current && windowStartMsRef.current != null) {

                        //console.log('buffer lastKnownPos ', dayjs(lastKnownPointerRef.current).format('HH:mm:ss'), dayjs(currentTimePointer).format('HH:mm:ss'))
                        const relSec = (lastKnownPointerRef.current - windowStartMsRef.current) / 1000;//This might be the candidate
                        video.currentTime = relSec;
                    } else {
                        video.currentTime = currentTime + 0.5; // fallback small bump
                    }
                }
            }

            // Wait a bit and try again
            setTimeout(retry, 1500);
        };

        retry();
    };


    const destroyHLS = (index) => {
        if (hlsRefs.current[index]) {
            hlsRefs.current[index].stopLoad();
            hlsRefs.current[index].detachMedia();
            hlsRefs.current[index].destroy();
            hlsRefs.current[index] = null;
        }

        if (hlsHighResRef.current) {
            hlsHighResRef.current.stopLoad();
            hlsHighResRef.current.detachMedia();
            hlsHighResRef.current.destroy();
            hlsHighResRef.current = null;
        }

        const video = videoRefs.current[index];
        if (video) {
            video.removeAttribute('src');
            video.load();
        }
    }

    const attachVideoListeners = (video, index) => {
        if (areListenersSetup[index]){
            return
        } else {
            setListenersSetup(prev => ({
            ...prev,
            [index]: true,
            }));
        }

        video.addEventListener("loadedmetadata", () => {
            updateVideoState(index, { ready: true });
        })

        video.addEventListener("pause", () => {
            hlsRefs.current[index].stopLoad()
        });

        video.addEventListener("ended", () => {
            updateVideoState(index, { paused: true });
        });

        video.addEventListener("canplay", () => {
            updateVideoState(index, { ready: true });
            const allReady = Object.values(videoStates).every(s => s?.ready);
            if (allReady && !playingRef.current) {
                playAll()
                playingRef.current = true
            }
        }
    );

        video.addEventListener("stalled", () => {
            updateVideoState(index, { paused: true });
        });

        video.addEventListener("timeupdate", () => {
            const windowStart = windowStartMsRef.current;
            if (windowStart != null) {
                const absMs = windowStart + video.currentTime * 1000;
                updateVideoState(index, { currentAbs: absMs });
                if (index === 0) {
                    setLeaderVideoCurrentTime(video.currentTime);
                }
            }
        })

        video.addEventListener("play", () => {
            updateVideoState(index, { ready: true });
        })
    };

    // NEW: Leader-follower sync checker for playback mode
    useEffect(() => {
        if (masterPlaybackModeRef.current !== "playback" || !playingRef.current || videoRefs.current.length === 0) {
            return;
        }

        const SYNC_THRESHOLD_SEC = 2; // 2 second desync tolerance
        const SYNC_INTERVAL_MS = 1000; // Check every 1 second

        const syncInterval = setInterval(() => {
            const leader = videoRefs.current[0];
            if (!leader || !linkedStreams.includes(+leader.id)) {
                return;
            }

            const leaderTime = leader.currentTime;
            // Check all follower videos
            for (let i = 1; i < videoRefs.current.length; i++) {
                const follower = videoRefs.current[i];
                if (!follower || !linkedStreams.includes(+follower.id)) {
                    continue
                }

                const timeDiff = Math.abs(follower.currentTime - leaderTime);
                if (timeDiff > SYNC_THRESHOLD_SEC) {
                    //console.log(`[Sync] Video ${i} is ${timeDiff.toFixed(2)}s out of sync. Correcting...`);
                    
                    // Seek follower to leader's position
                    try {
                        follower.currentTime = leaderTime;
                    } catch (err) {
                        //console.warn(`[Sync] Failed to sync video ${i}:`, err);
                    }
                }
            }
        }, SYNC_INTERVAL_MS);

        return () => clearInterval(syncInterval);
    }, [masterPlaybackModeRef.current, playingRef.current, linkedStreams]);

    // Universal seek 
    const requestSeek = (targetMs) => {
        const relSec = (targetMs - windowStartMs) / 1000;
        pauseAll()

        if (masterPlaybackModeRef.current === "playback" && windowStartMs != null) {

            // if relSec is inside buffered range → seek
            const v = videoRefs.current[0];
            if (v && v.seekable.length > 0) {
                const start = v.seekable.start(0);
                const end = v.seekable.end(0);

                if (targetMs >= Date.now()) {
                    goLiveAll()
                    return
                }

                if (relSec >= start && relSec <= end) {
                    videoRefs.current.forEach((v, i) => {
                        if (v && linkedStreams.includes(+v.id)) {
                            try {
                                v.currentTime = relSec;
                            } catch (err) {
                                //console.warn(`[${i}] seek failed`, err);
                            }
                        }
                    });
                    setCurrentTimePointer(Math.trunc(targetMs));
                    setScrollWheelStart({ time: Math.trunc(targetMs), nonce: Date.now() });
                    lastKnownPointerRef.current = targetMs;

                    return;
                }
            }
        }
        
        lastKnownPointerRef.current = targetMs;
        setMasterIsLive(false)
        masterPlaybackModeRef.current = "playback"
        setCurrentTimePointer(Math.trunc(targetMs));
        setWindowStartMs(Math.trunc(targetMs));
        setMasterPlaybackStart(Math.trunc(targetMs / 1000));
        setScrollWheelStart({ time: Math.trunc(targetMs), nonce: Date.now() });
        setGroupPlaying(true)

    };

    const updateVideoState = (index, partial) => {
        setVideoStates(prev => ({
            ...prev,
            [index]: { ...(prev[index] || {}), ...partial }
        }));
    };

    const playAll = () => {

        setGroupPlaying(true);

        hlsRefs.current.forEach(hls => {
            if (hls && hls.startLoad) {
                hls.startLoad();
            }
        });
        const playPromises = videoRefs.current.map(v => v?.play?.());
        Promise.allSettled(playPromises).then(() => {
            playingRef.current = true
        });
    };


    const pauseAll = () => {

        setGroupPlaying(false)
        videoRefs.current.forEach(v => {
            v?.pause?.()
    })
        playingRef.current = false
        setMasterIsLive(false)
    };

    const stepBackwardsAll = () => {
        requestSeek(currentTimePointer - 10000)
    }

    const stepForwardAll = () => {
        requestSeek(currentTimePointer + 10000)
    }

    const getMasterDuration = () => {
        return Math.max(...videoRefs.current.map(video => video.seekable.length > 0
            ? video.seekable.end(0) - video.seekable.start(0)
            : 0
        ));
    }

    const setSpeedAll = (speed) => videoRefs.current.forEach(video => {
        masterSpeed.current = speed
        if (speed == "1") {
            isHighspeed.current = false 
        } else {
            isHighspeed.current = true 
        }
        video.playbackRate = speed; //seems duplicative?
    })

    const goLiveAll = () => {
        var current_time = 0

        if (!masterIsLive) {
            setMasterIsLive(true)
            masterPlaybackModeRef.current = 'live'
            setVideoStates({})
        }

        playingRef.current = true
        setSpeedAll("1")
        setScrollWheelStart({time: Date.now()})
        
        videoRefs.current.forEach(video => {
            if (video.seekable.length > 0) {
                video.currentTime = video.seekable.end(0) - 4;
                //setMasterSeekPosition(video.currentTime)
                current_time = video.currentTime
            }
            video.play()
        })
        setMasterSeekPosition(current_time)
    };

    useEffect(() => {
        if (videoRefs.current.length > 0) {
            const duration = getMasterDuration();
            setMasterDuration(duration);
        }
    }, [videoRefs.current]); // Runs when videoRefs change

    // MODIFIED: Update currentTimePointer based on mode
    useEffect(() => {
        if (masterPlaybackModeRef.current === "live") {
            const id = setInterval(() => {
                if (masterIsLive && playingRef.current) {
                    const newTime = Date.now();
                    setCurrentTimePointer(newTime);
                    lastKnownPointerRef.current = newTime;
                }
            }, 1000);
            return () => clearInterval(id);
        } else {
            // playback: sync from leader video (video[0])
            const id = setInterval(() => {
                const v = videoRefs.current[0];
                if (v && v.currentTime != null && windowStartMs != null && playingRef.current) {
                    const new_time = windowStartMs + v.currentTime * 1000;
                    setCurrentTimePointer(new_time);
                    lastKnownPointerRef.current = new_time;
                }
            }, 100); // Update more frequently for smoother seekbar tracking
            return () => clearInterval(id);
        }
    }, [masterPlaybackModeRef.current, playingRef, windowStartMs]);

    return (
        <VideoContext.Provider value={{
            videoRefs,
            playAll,
            pauseAll,
            playing,
            stepBackwardsAll,
            stepForwardAll,
            masterSeekPosition,
            masterDuration,
            getMasterDuration,
            goLiveAll,
            masterLivePosition,
            setMasterLivePosition,
            linkedStreams,
            setLinkedStreams,
            masterIsLive,
            setMasterIsLive,
            masterIsSeeking,
            setMasterIsSeeking,
            masterPlaybackModeRef,
            masterPlaybackStart,
            setMasterPlaybackStart,
            masterPlaybackEnd,
            setMasterPlaybackEnd,
            initializeHLS,
            streamErrors,
            hlsRefs,
            destroyHLS,
            cancelSignal,
            setCancelSignal,
            streamTokens,
            setStreamTokens,
            setStreamErrors,
            streamLoading,
            setStreamLoading,
            requestSeek,
            windowStartMs,
            currentTimePointer,
            setWindowStartMs,
            scrollWheelStart,
            setCurrentTimePointer,
            setPlaying,
            lastKnownPointerRef,
            videoStates,
            setVideoStates,
            updateVideoState,
            groupPlaying,
            setGroupPlaying,
            setScrollWheelStart,
            attachVideoListeners,
            masterSpeed,
            setSpeedAll,
            playingRef,
            leaderVideoCurrentTime, // NEW: Export for seekbar
            isHighspeed,
        }}>
            {children}
        </VideoContext.Provider>
    );
};

export const useVideoContext = () => useContext(VideoContext);